import {
  useEffect,
  useId,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Combobox, type ComboboxOption } from '@/components/ui/Combobox';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { cx } from '@/lib/cx';

/**
 * One record column, edited where it is read.
 *
 * ## What this owns, and what the caller owns
 *
 * This primitive owns the **interaction**: idle → editing → saving →
 * saved/failed, the keyboard contract, the focus moves, and the rule that an
 * untouched field never fires a request. It owns no knowledge of the API at
 * all — no endpoint, no payload shape, no `ApiError`. That belongs to the
 * caller, which is the only place that knows which request key this column
 * writes to and how a 403 on that module should read.
 *
 * So the caller hands in `onSave`, which does the write and **reports** rather
 * than throws: `saved`, `warning` (the write completed but did not do what was
 * asked), `partial` (the write landed and something AFTER it did not), or
 * `error`. A thrown error is caught anyway and treated as `error`, because a
 * primitive that loses a save silently is worse than one that says something
 * imprecise.
 *
 * ## The three rules that are correctness rather than polish
 *
 * - **No write when nothing changed.** Blurring an untouched field must not
 *   PATCH: on this API an empty PATCH is a 422, and re-sending an unchanged
 *   picklist id can be one too. So a commit whose value equals the stored one
 *   is a state reset, never a request. Without this, an idle user tabbing
 *   through a record would write every column.
 * - **A failed save keeps what was typed.** The editor stays open with the
 *   draft intact and the message on the field. Snapping back to the stored
 *   value would throw away someone's typing *and* leave the record looking as
 *   though it had saved.
 * - **A save always says so.** A silent save is indistinguishable from a broken
 *   one, so success gets a short-lived acknowledgement and failure gets a
 *   message that stays until the next attempt.
 */

export type InlineFieldKind = 'text' | 'multiline' | 'picker';

/** Same shape the `Combobox` takes — reference options are `{ id, name }` rows. */
export type InlineFieldOption = ComboboxOption;

/**
 * What the caller's write reported.
 *
 * `warning` is not a failure and it is not a validation problem. It is for a
 * write that is OVER — the record on the server is now authoritative and the
 * draft can never become it — but that did not do what was asked. The editor
 * closes (there is nothing left to retry) and the message stays, because
 * nobody would otherwise notice. Three unrelated causes land here:
 *
 * - **The key was stripped.** A 200 that changed nothing. Only a read-back of
 *   the saved record can tell. (`PATCH /companies/:id` is `.strict()` now, so
 *   this arrives as the rejection below rather than as a silent 200 — but the
 *   read-back still answers a different question: *accepted and not applied*.)
 * - **The key was REJECTED.** Quotes, invoices, deals, deliverables, contacts
 *   and companies are `.strict()`, so the same mistake is a 422 whose detail
 *   carries `path: ''` plus a `key` naming the offender.
 * - **The value was refused by a system this form cannot reach.** A company's
 *   `currencyId` is validated against what the Xero organisation ENABLES, so
 *   `XERO_CURRENCY_NOT_ENABLED` refuses a perfectly good currency until an
 *   accountant enables it in Xero. Nothing typed here can satisfy that, so it is
 *   `warning` and not `error` — see `describeCurrencyFailure`.
 *
 * ⚠️ **Neither is `error`.** An unroutable rejection is not about the value the
 * user typed: marking the control `invalid` states something false about the
 * draft, and holding the editor open invites a retype that will be refused
 * identically. The harm the unroutable-key rule exists to prevent CANNOT arise
 * here — this primitive has one control, one message slot and no "N fields need
 * attention" count, so the message always renders on the one field it concerns.
 * Report it as `warning`.
 *
 * ## `partial` — the write LANDED and something after it did not
 *
 * Added 2026-08-31, for a column whose save has a SECOND request behind it: a
 * deal's **Next task** is a `d_deals` column *and* a note on the deal and on its
 * company, and `/notes` is one of the three modules the server genuinely
 * enforces by permission set — so the note can be refused for somebody who may
 * perfectly well write the deal.
 *
 * ⚠️ **It is the exact inverse of `warning`, which is why it could not reuse
 * it.** `warning` says *the request is over and your value did not take* — its
 * callers end on "Nothing was saved." `partial` says *your value DID take, and
 * a follow-up did not*. Reporting one as the other is the harm the two-phase
 * write rule exists to prevent, in both directions:
 *
 * - as `error` or `warning`, the reader concludes the field did not save and
 *   retypes it — which writes a second note and still reports a failure;
 * - as `saved`, the follow-up failure is silent, and nobody ever learns the
 *   timeline entry they are relying on does not exist.
 *
 * So it closes the editor (the write is over, there is nothing to retry here),
 * keeps its message until the next attempt (a fading one would be missed), and
 * renders under the **`Saved.` lead this primitive supplies itself** — no
 * caller can forget to say the value landed. `message` is therefore about the
 * FOLLOW-UP alone, in the server's own words.
 */
export type InlineSaveResult =
  | { status: 'saved'; message?: string }
  | { status: 'warning'; message: string }
  | { status: 'partial'; message: string }
  | { status: 'error'; message: string };

interface Notice {
  tone: 'success' | 'warning' | 'error' | 'partial';
  text: string;
}

/** How long a "Saved" acknowledgement stays on screen. */
const SAVED_NOTICE_MS = 4000;

/**
 * The acknowledgement, in one place.
 *
 * It is the default `saved` line AND the lead on every `partial` one, so a
 * reader cannot meet two different sentences for the same fact — and a caller
 * reporting a refused follow-up cannot omit the half that says the field
 * itself is safe.
 */
const SAVED_LEAD = 'Saved.';

const EM_DASH = '—';

export interface InlineFieldProps {
  /**
   * The field's name, in the reader's words. It is the trigger's accessible
   * name ("Edit Owner: Ana Rivera") and the editor's label, so a control in a
   * `<dd>` is never announced as unlabelled — a `<dt>` is not a `<label>`.
   */
  label: string;
  /**
   * The stored value as a string. `''` is empty, which the caller maps to
   * `null` on the wire where the column is nullable.
   */
  value: string;
  /**
   * Performs the write and reports the outcome. Never called when the value is
   * unchanged, and never called with anything the local `validate` refused.
   */
  onSave: (next: string) => Promise<InlineSaveResult>;
  kind?: InlineFieldKind;
  /**
   * The read view. Defaults to the value itself — pass this for anything that
   * reads as more than its own text (a badge, an avatar, a wrapped block).
   * It must contain no interactive elements: it renders inside the trigger.
   */
  display?: ReactNode;
  /** How an empty value reads when idle. */
  emptyLabel?: string;
  /**
   * Editing is unavailable — no permission, nothing to pick from, or a column
   * that is not this record's to write. The value still renders, as plain text
   * with no control and no affordance, because a trigger that cannot do
   * anything is worse than no trigger.
   */
  disabled?: boolean;
  /**
   * Why it cannot be edited, in one sentence, shown under the value.
   *
   * ⚠️ **`disabled` alone renders no explanation at all**, so three quite
   * different fields look identical: one the reader may not edit, one nobody
   * may edit, and one that simply reads as an ordinary value. Those want
   * different sentences and only the caller knows which — `denialReason()` for
   * a lost permission, and plain words for a derived column ("Set by the
   * company's account lead." / "Set by the Xero link.").
   *
   * The distinction is not cosmetic. A derived column reads as a permission
   * problem otherwise, and someone goes hunting an administrator for access
   * that would change nothing: `accountLead` is written on the company, and
   * `xeroContactId` by linking to a Xero contact. Say where the field IS
   * editable; a field that is nobody's to edit says that instead.
   *
   * Omitted, nothing renders and the field behaves exactly as it did before
   * this prop existed. It has no effect unless `disabled` is also set.
   */
  disabledReason?: string;
  /**
   * Rendered BESIDE the trigger, never inside it: a `mailto:` or a website link
   * is interactive, and interactive content cannot nest in a button. This is
   * how a value stays clickable when the whole row is an edit trigger.
   */
  action?: ReactNode;
  /**
   * The client-side rule, mirrored from the modal that already encodes it.
   * Return a message to refuse, `null` to allow. Refusing keeps the editor open
   * with the draft and fires no request.
   */
  validate?: (next: string) => string | null;
  /** Text kinds. Mirrors the server's limit so the control stops where the API would. */
  maxLength?: number;
  /**
   * `'date'` is for the plain `YYYY-MM-DD` columns, and it is the same pairing
   * the modals already use — a native date control alongside `isRealDate()` in
   * `validate` (`CycleFormModal`, `DealFormModal`). It is the right control
   * precisely BECAUSE it never involves a `Date`: the element's value is
   * `YYYY-MM-DD` text, so nothing here ever writes `new Date('2026-05-31')` and
   * renders 30 May west of Greenwich.
   *
   * ⚠️ Pass `validate` with it. A part-typed date reads back as `''`, which is
   * indistinguishable from a cleared field — on a column that cannot be cleared
   * that is a silent write, and only the caller's rule stops it.
   */
  type?: 'text' | 'email' | 'tel' | 'date';
  /**
   * `'decimal'` is the money one, matching `DealFormModal` and
   * `LineItemFormModal`. It buys the numeric keypad below `sm` and nothing
   * else — the exactness is already structural, because this primitive is
   * string-in/string-out and never puts a value through `Number()`.
   */
  inputMode?: 'text' | 'url' | 'decimal' | 'numeric';
  autoComplete?: string;
  placeholder?: string;
  rows?: number;
  /** Picker kind. */
  options?: InlineFieldOption[];
  optionsLoading?: boolean;
  /** Label for the "no selection" row — `null` is a real answer on these columns. */
  emptyOptionLabel?: string;
  searchPlaceholder?: string;
  footnote?: string;
  /** Picker kind: also accept a typed value not in `options` (Combobox `freeText`). */
  freeText?: boolean;
}

export function InlineField({
  label,
  value,
  onSave,
  kind = 'text',
  display,
  emptyLabel = EM_DASH,
  disabled = false,
  disabledReason,
  action,
  validate,
  maxLength,
  type = 'text',
  inputMode,
  autoComplete,
  placeholder,
  rows = 4,
  options,
  optionsLoading = false,
  emptyOptionLabel,
  searchPlaceholder,
  footnote,
  freeText = false,
}: InlineFieldProps) {
  const uid = useId();
  const controlId = `${uid}-control`;
  const statusId = `${uid}-status`;

  const [phase, setPhase] = useState<'idle' | 'editing' | 'saving'>('idle');
  const [draft, setDraft] = useState(value);
  const [notice, setNotice] = useState<Notice | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  /*
   * Focus is moved on the transitions the user asked for and on no others.
   * Refs rather than state: a failed save re-renders the editor, and refocusing
   * then would yank the caret back from wherever the user had moved on to.
   */
  const wantsEditorFocus = useRef(false);
  const wantsTriggerFocus = useRef(false);
  /** Two quick commits can resolve out of order; only the newest may land. */
  const saveSeq = useRef(0);

  useEffect(() => {
    if (phase !== 'editing' || !wantsEditorFocus.current) return;
    wantsEditorFocus.current = false;
    const control = editorRef.current?.querySelector<HTMLElement>('input, textarea, button');
    if (!control) return;
    control.focus();
    /*
     * A picker's editor is itself a button that opens a list. Opening it here
     * makes editing one interaction instead of two — the whole point of editing
     * in place — and it is the same gesture the user already made.
     */
    if (control.tagName === 'BUTTON') control.click();
  }, [phase]);

  useEffect(() => {
    if (phase !== 'idle' || !wantsTriggerFocus.current) return;
    wantsTriggerFocus.current = false;
    triggerRef.current?.focus();
  }, [phase]);

  useEffect(() => {
    if (notice?.tone !== 'success') return;
    const timer = setTimeout(() => setNotice(null), SAVED_NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  function beginEditing() {
    if (disabled || phase !== 'idle') return;
    setDraft(value);
    // A message about the last attempt says nothing about this one.
    setNotice(null);
    wantsEditorFocus.current = true;
    setPhase('editing');
  }

  function revert(returnFocus: boolean) {
    setDraft(value);
    setNotice(null);
    wantsTriggerFocus.current = returnFocus;
    setPhase('idle');
  }

  async function commit(raw: string, returnFocus: boolean) {
    /*
     * Only a live editor may commit. This also covers the blur the browser
     * fires as the editor unmounts on a successful save — without the guard,
     * that stray blur would run as an unchanged commit and wipe the "Saved"
     * acknowledgement it had just put on screen.
     */
    if (phase !== 'editing') return;

    /*
     * A picklist value is an id and is compared exactly; text is compared and
     * sent trimmed, the same way the modal diffs it, so a stray space is never
     * a change and can never become one on the wire.
     */
    const next = kind === 'picker' ? raw : raw.trim();
    const stored = kind === 'picker' ? value : value.trim();

    if (next === stored) {
      revert(returnFocus);
      return;
    }

    const refusal = validate?.(next) ?? null;
    if (refusal) {
      setDraft(raw);
      setNotice({ tone: 'error', text: refusal });
      return;
    }

    const ticket = saveSeq.current + 1;
    saveSeq.current = ticket;
    setDraft(next);
    setNotice(null);
    setPhase('saving');

    let result: InlineSaveResult;
    try {
      result = await onSave(next);
    } catch (caught) {
      result = {
        status: 'error',
        message: caught instanceof Error ? caught.message : 'The change could not be saved.',
      };
    }
    if (saveSeq.current !== ticket) return;

    /*
     * ⚠️ An exhaustive switch with a `never` default, not a chain of ternaries.
     * The chain it replaces treated every status it did not name as a clean
     * success, so a FIFTH outcome added later would have rendered "Saved." over
     * whatever it was reporting — silently, with a green `tsc`. This is the one
     * place that decides what an outcome looks like, so it is the one place
     * that must refuse to compile when the union grows.
     */
    switch (result.status) {
      case 'error':
        // The draft stays exactly as typed, and the field stays open on it.
        setNotice({ tone: 'error', text: result.message });
        setPhase('editing');
        return;
      case 'warning':
        settle({ tone: 'warning', text: result.message }, returnFocus);
        return;
      case 'partial':
        // The lead is this primitive's, never the caller's: `message` is about
        // the follow-up alone and must not be the only thing on the line.
        settle({ tone: 'partial', text: `${SAVED_LEAD} ${result.message}` }, returnFocus);
        return;
      case 'saved':
        settle({ tone: 'success', text: result.message ?? SAVED_LEAD }, returnFocus);
        return;
      default: {
        const unhandled: never = result;
        throw new Error(`Unhandled inline save outcome: ${JSON.stringify(unhandled)}`);
      }
    }
  }

  /** The three outcomes that END the edit: close the editor, keep the line. */
  function settle(reported: Notice, returnFocus: boolean) {
    wantsTriggerFocus.current = returnFocus;
    setNotice(reported);
    setPhase('idle');
  }

  function onEditorKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    /*
     * A request in flight cannot be recalled, so Escape does not pretend to.
     * Reverting here would leave the field showing the old value while a write
     * of the new one was still on its way to the server.
     */
    if (phase !== 'editing') return;
    if (event.key === 'Escape') {
      event.preventDefault();
      revert(true);
      return;
    }
    // In a textarea Enter is a newline and must stay one, so that editor
    // commits on blur alone.
    if (event.key === 'Enter' && kind !== 'multiline') {
      event.preventDefault();
      void commit(draft, true);
    }
  }

  function onEditorBlur(event: FocusEvent<HTMLDivElement>) {
    // The text kinds commit from their own control's blur. This handler exists
    // for the picker, whose control is a button with no value of its own.
    if (kind !== 'picker' || phase !== 'editing') return;
    const next = event.relatedTarget;
    if (next instanceof HTMLElement) {
      if (event.currentTarget.contains(next)) return;
      /*
       * The picker's list is portalled to <body>, so focus moving INTO it looks
       * from here like focus leaving the field. Its search box is the element
       * that takes focus, and it is the one that carries this role.
       */
      if (next.getAttribute('role') === 'combobox') return;
    }
    revert(false);
  }

  const shown =
    display ?? (value.trim() === '' ? <span className="text-fg-subtle">{emptyLabel}</span> : value);

  /*
   * One line for every outcome, so a save can never report itself twice or in
   * two voices. `alert` and `status` each imply their own politeness, so
   * neither gets a contradicting `aria-live` alongside it.
   *
   * ⛔ **THE EDITING HINT WAS REMOVED — owner's helper-text ruling, 2026-09-05.**
   * While `phase === 'editing'` and nothing else had anything to report, this
   * line carried one of three keyboard sentences, chosen by `kind`:
   *
   * - multiline: `Saves when you leave the field. Esc cancels.`
   * - picker:    `Choosing saves it. Esc cancels.`
   * - otherwise: `Enter saves. Esc cancels.`
   *
   * ⚠️ **THIS CONTROL STILL HAS NO SAVE BUTTON AND STILL COMMITS ON BLUR.** That
   * is a WRITE with no visible affordance, and it is now entirely undiscoverable:
   * somebody who types and clicks away has saved, and somebody who types and
   * presses Escape has not, with nothing on screen distinguishing the two. The
   * behaviour is unchanged — only the sentence went.
   *
   * ⚠️ **`kind` is still read** for the control itself (multiline vs picker vs
   * single-line) — do not remove the prop chasing this.
   *
   * The tone on the two SYNTHESISED lines below is inert: each is reached only
   * while `notice` is null, and the class picks `text-fg-subtle` for that case
   * before it ever looks at a tone. `isProblem` and `isSaved` both require a real
   * `notice` too, so neither can borrow a glyph.
   *
   * The `disabledReason` branch occupies a slot that was `null` before: idle, not
   * editing, nothing to report. A lingering "Saved." still wins over it, so a
   * field that goes read-only under a changed role does not lose the
   * acknowledgement of the write that just landed.
   */
  const line: Notice | null =
    phase === 'saving'
      ? { tone: 'success', text: 'Saving…' }
      : (notice ??
        (disabled && disabledReason !== undefined
          ? { tone: 'success', text: disabledReason }
          : null));
  /*
   * ⚠️ Three derived flags, not one. `isProblem` used to be "any tone that is
   * not success" and drove all three of the control's `invalid`, the alert role
   * and the alert glyph — which is exactly the collapse `partial` must not
   * inherit: a field that SAVED must not be marked invalid, and must not wear
   * the glyph that says the value did not take.
   */
  /** The value did not take. Marks the control and wears the alert glyph. */
  const isProblem =
    phase !== 'saving' && (notice?.tone === 'error' || notice?.tone === 'warning');
  /** The value took. `partial` wears the tick too — that half really did land. */
  const isSaved = phase === 'idle' && (notice?.tone === 'success' || notice?.tone === 'partial');
  /**
   * Announced rather than merely available. `partial` is announced despite
   * wearing a tick: it carries a refusal nobody asked to be told about, and it
   * arrives on a field the reader is already walking away from.
   */
  const isAlert = isProblem || (phase !== 'saving' && notice?.tone === 'partial');
  const describedBy = line ? statusId : undefined;

  return (
    <div className="grid min-w-0 gap-1">
      {phase === 'idle' ? (
        <div className="flex min-w-0 items-start gap-1">
          {disabled ? (
            <span className="min-w-0 flex-1">{shown}</span>
          ) : (
            <button
              type="button"
              ref={triggerRef}
              onClick={beginEditing}
              aria-describedby={describedBy}
              className={cx(
                'group -mx-1.5 -my-1 flex min-w-0 flex-1 items-start gap-1.5 rounded-md px-1.5 py-1 text-left',
                'transition-colors duration-150 ease-out motion-reduce:transition-none',
                'hover:bg-surface-hover',
              )}
            >
              {/* The trigger has to name the field: the visible <dt> is not a label. */}
              <span className="sr-only">Edit {label}: </span>
              <span className="min-w-0 flex-1">{shown}</span>
              <PencilGlyph />
            </button>
          )}
          {action}
        </div>
      ) : (
        <div
          ref={editorRef}
          onKeyDown={onEditorKeyDown}
          onBlur={onEditorBlur}
          aria-busy={phase === 'saving' || undefined}
          className="grid min-w-0 gap-1"
        >
          {kind === 'picker' ? (
            /* A <label> cannot name a button, so the editor is a named group. */
            <div role="group" aria-label={label} className="min-w-0">
              <Combobox
                id={controlId}
                value={draft}
                options={options ?? []}
                loading={optionsLoading}
                disabled={phase === 'saving'}
                invalid={isProblem}
                aria-describedby={describedBy}
                {...(emptyOptionLabel === undefined ? {} : { emptyOptionLabel })}
                {...(searchPlaceholder === undefined ? {} : { searchPlaceholder })}
                {...(footnote === undefined ? {} : { footnote })}
                {...(placeholder === undefined ? {} : { placeholder })}
                freeText={freeText}
                onChange={(next) => void commit(next, true)}
              />
            </div>
          ) : (
            <>
              <label htmlFor={controlId} className="sr-only">
                {label}
              </label>
              {kind === 'multiline' ? (
                <Textarea
                  id={controlId}
                  rows={rows}
                  value={draft}
                  maxLength={maxLength}
                  placeholder={placeholder}
                  readOnly={phase === 'saving'}
                  invalid={isProblem}
                  aria-describedby={describedBy}
                  onChange={(event) => setDraft(event.target.value)}
                  onBlur={() => void commit(draft, false)}
                />
              ) : (
                <Input
                  id={controlId}
                  type={type}
                  inputMode={inputMode}
                  value={draft}
                  maxLength={maxLength}
                  autoComplete={autoComplete}
                  placeholder={placeholder}
                  readOnly={phase === 'saving'}
                  invalid={isProblem}
                  aria-describedby={describedBy}
                  onChange={(event) => setDraft(event.target.value)}
                  onBlur={() => void commit(draft, false)}
                />
              )}
            </>
          )}
        </div>
      )}

      {line && (
        <p
          id={statusId}
          role={isAlert ? 'alert' : 'status'}
          className={cx(
            'm-0 flex min-w-0 items-start gap-1 text-xs [overflow-wrap:anywhere]',
            phase === 'saving' || notice === null ? 'text-fg-subtle' : noticeToneClass(notice.tone),
          )}
        >
          {/* Never colour alone: the glyph and the wording each carry it too. */}
          {isProblem && <AlertGlyph />}
          {isSaved && <CheckGlyph />}
          <span className="min-w-0">{line.text}</span>
        </p>
      )}
    </div>
  );
}

/**
 * The status line's colour, per tone.
 *
 * ⚠️ Exhaustive with a `never` default, for the reason the commit switch is: a
 * tone the chain it replaces did not name fell through to `text-success`, so a
 * new outcome would have been painted as a clean save. `partial` shares
 * `warning`'s amber deliberately — something wants attention — and is told
 * apart from it by the glyph and the wording, never by colour alone.
 */
function noticeToneClass(tone: Notice['tone']): string {
  switch (tone) {
    case 'error':
      return 'text-danger';
    case 'warning':
    case 'partial':
      return 'text-warning';
    case 'success':
      return 'text-success';
    default: {
      const unhandled: never = tone;
      throw new Error(`Unhandled notice tone: ${String(unhandled)}`);
    }
  }
}

function glyphProps(className: string) {
  return {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    className,
  };
}

/**
 * The edit affordance. Faintly visible at rest, brightening on hover or focus.
 *
 * ⚠️ **It used to be `opacity-0` above `sm`, revealed only on hover, and that
 * was reported as a missing feature (2026-08-29).** The reasoning was that a
 * record should read as a record rather than as a form, which is a real
 * concern and is what the low resting opacity still answers. But on a pointer
 * device it made every editable value look like static text: the owner opened
 * a company's Xero sales defaults, saw `Use organisation settings` with no
 * control beside it, and reported that the field could not be changed. It
 * could — there was simply nothing on screen saying so until the pointer
 * happened to cross it.
 *
 * **A missing affordance is indistinguishable from a removed feature**, which
 * this codebase had already learnt once from the companies kanban "+" menu and
 * recorded in `CLAUDE.md`. Below `sm` the glyph was always visible for exactly
 * this reason — there is no hover to reveal it with — so the fix is to adopt
 * the touch behaviour everywhere rather than to invent a third treatment.
 *
 * Do not put it back to `opacity-0`. If a surface genuinely needs a value to
 * read as inert, that surface wants `disabled` with a `disabledReason`, which
 * renders no trigger and no glyph at all.
 */
function PencilGlyph() {
  return (
    <svg
      {...glyphProps(
        cx(
          'mt-0.5 h-3.5 w-3.5 shrink-0 text-fg-subtle opacity-60',
          'transition-opacity duration-150 ease-out motion-reduce:transition-none',
          'group-hover:opacity-100 group-focus-visible:opacity-100',
        ),
      )}
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

function AlertGlyph() {
  return (
    <svg {...glyphProps('mt-px h-3.5 w-3.5 shrink-0')}>
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
    </svg>
  );
}

function CheckGlyph() {
  return (
    <svg {...glyphProps('mt-px h-3.5 w-3.5 shrink-0')}>
      <path d="m5 12 5 5L20 7" />
    </svg>
  );
}
